import { z } from 'zod';
import { refersTo } from '../common/refers-to';

const WorkspaceIdPathParamsSchema = z.object({ workspaceId: z.uuid() });
const LeadPartnerIdPathParamsSchema = z.object({ workspaceId: z.uuid(), id: z.uuid() });

// Workspace-wide named partner a lead can be attributed to (Source = Partner).
// Lives in the lead module; `slug` is derived from the name and unique per
// workspace. Deactivation is the only retirement path — no hard-delete.
export const LeadPartnerSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  active: z.boolean(),
  // True once the partner has issued and linked an API key it pushes leads
  // through. The secret itself is never exposed here — only at issuance.
  hasApiKey: z.boolean(),
  createdAt: z.iso.datetime(),
});
export type LeadPartner = z.infer<typeof LeadPartnerSchema>;

export const CreateLeadPartnerBodySchema = z
  .object({
    name: z.string().trim().min(1).max(120),
  })
  .strict();
export type CreateLeadPartnerBody = z.infer<typeof CreateLeadPartnerBodySchema>;

export const ListLeadPartnersPathParamsSchema = WorkspaceIdPathParamsSchema;
export const ListLeadPartnersResponseSchema = z.object({ data: z.array(LeadPartnerSchema) });
export type ListLeadPartnersResponse = z.infer<typeof ListLeadPartnersResponseSchema>;

export const CreateLeadPartnerPathParamsSchema = WorkspaceIdPathParamsSchema;
export const CreateLeadPartnerResponseSchema = LeadPartnerSchema;

export const LeadPartnerActionPathParamsSchema = LeadPartnerIdPathParamsSchema;
export const LeadPartnerResponseSchema = LeadPartnerSchema;

// Issue a lead-scoped API key for a partner. The acting member owns the key
// (member-bound); the partner links it via `api_key_id`. The plaintext
// `secret` is returned exactly once and never persisted nor re-exposed.
export const IssueLeadPartnerKeyPathParamsSchema = LeadPartnerIdPathParamsSchema;
export const IssueLeadPartnerKeyResponseSchema = z.object({
  apiKeyId: refersTo('api-key', z.uuid()),
  name: z.string(),
  secret: z.string(),
  leadPartner: LeadPartnerSchema,
});
export type IssueLeadPartnerKeyResponse = z.infer<typeof IssueLeadPartnerKeyResponseSchema>;
